
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { LinkedUser } from '@/types/broker.types';

export const useLinkedUsers = () => {
  const { user, userRole } = useAuth();
  const { toast } = useToast();
  const [linkedUsers, setLinkedUsers] = useState<LinkedUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  
  useEffect(() => {
    const fetchLinkedUsers = async () => {
      if (!user || userRole !== 'broker') return;
      
      setIsLoading(true);
      try {
        const { data: linksData, error: linksError } = await supabase
          .from('broker_user_links')
          .select('id, user_id, broker_id, created_at')
          .eq('broker_id', user.id)
          .order('created_at', { ascending: false });
          
        if (linksError) throw linksError;
        
        if (!linksData || linksData.length === 0) {
          setLinkedUsers([]);
          setIsLoading(false);
          return;
        }
        
        const processedUsers: LinkedUser[] = [];
        
        for (const link of linksData) {
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select('first_name, last_name, email')
            .eq('id', link.user_id)
            .single();
            
          if (profileError) {
            console.error('Error fetching profile for user:', link.user_id, profileError);
            continue;
          }
          
          const { data: applicationsData, error: applicationsError } = await supabase
            .from('applications')
            .select('id, status, progress, company_legal_name, created_at')
            .eq('user_id', link.user_id)
            .order('created_at', { ascending: false });
            
          if (applicationsError) {
            console.error('Error fetching applications for user:', link.user_id, applicationsError);
          }
          
          processedUsers.push({
            id: link.id,
            user_id: link.user_id,
            broker_id: link.broker_id,
            created_at: link.created_at,
            profile: {
              first_name: profileData.first_name || '',
              last_name: profileData.last_name || '',
              email: profileData.email || '',
            },
            applications: applicationsData || []
          });
        }
        
        setLinkedUsers(processedUsers);
      } catch (error: any) {
        console.error('Error fetching linked users:', error);
        toast({
          title: 'Error fetching linked clients',
          description: error.message,
          variant: 'destructive'
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchLinkedUsers();
  }, [user, userRole, toast]);
  
  return {
    linkedUsers,
    isLoading
  };
};
